import * as O from 'fp-ts/lib/Option';
import {Box, Paper, Table as MaterialTable, TableBody as MaterialTableBody, TableFooter, TextField, Typography, Button} from '@material-ui/core';
import {isLoaded, matchAsyncPage} from 'Core/models/Async';
import * as A from 'fp-ts/lib/Array';
import {pipe} from 'fp-ts/lib/pipeable';
import {useDispatch, useSelector} from 'react-redux';
import * as I from 'immutable';
import React, {useEffect, useState} from 'react';
import Loading from 'Shared/components/Loading';
import {SharedPagedProps} from 'Shared/components/PagedView/PagedView';
import 'Shared/components/PagedView/Table/Table.sass';
import TableBody from 'Shared/components/PagedView/Table/TableBody';
import TableHeader from 'Shared/components/PagedView/Table/TableHeader';
import VisibleColumnsDrawer from 'Shared/components/PagedView/Table/VisibleColumnsDrawer';
import {LocalStorageKey, fromLocalStorage} from 'Shared/utilities/localStorage';
import {safeParseJson} from 'Shared/utilities/parsers';
import {flow} from 'fp-ts/lib/function';
import {ReducerState} from 'rootReducer';
import {makeStyles} from '@material-ui/core/styles';
import Snackbar from '@mui/material/Snackbar';
import { Alert } from '@material-ui/lab';
import { changeCorrespondenceFilter } from 'Agent/Policies/ducks/policyCorrespondence';
import { RouteComponentProps, withRouter } from 'react-router-dom';


export type ReactWindowProps<TData> = {
  index: number;
  dataItem: TData;
};

export const useSnackBarStyles = makeStyles(theme => ({
  snackBar: {
    borderRadius: 0,
    textAlign: 'justify',
    top: '50%',
    backgroundColor: 'transparent',
    margin: '0 1.5rem 0 .8rem',
    right: 0,
    position: 'relative!important' as 'relative',
    transform: 'translate(-50%, -50%)',
    [theme.breakpoints.down('md')]: {
      width: '25%',
    },

    [theme.breakpoints.down('xs')]: {
      top: '40%',
      transform: 'translate(0%, 0%)',
      width: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      width: '20%',
    },
  },

  alertInfo: {
    display: 'flex',
    color: 'rgba(0, 0, 0, 0.87)',
    alignItems: 'flex-start',
    marginBottom: '0px',
    borderRadius: 0,
    width: '100%',
    backgroundColor: '#D9EEFF',
    '& .MuiAlert-icon': {
      color: '#2196F3',
      width: "20%",
      height: "20%",
      '& .MuiSvgIcon-root': {
        height: '100%',
        width: '100%'
      }
    },
  },
}));

export type TableProps<TData> = SharedPagedProps<TData> & RouteComponentProps;
export const ColumnWidthsContext = React.createContext(I.Map<string, number>());

export const ROW_SIZE = 48;
const NoResultsMessage = <div className="paged-table__content">No Results Available.</div>;
const isScrollbarShowing = (height: number, rowCount: number) => height >= rowCount * ROW_SIZE;

type SavedVisibilityPreferencesDictionary = {
  [userId: string]: {
    [pagedViewKey: string]: I.Map<string, boolean>;
  };
};

export const getSavedColumnVisibilityPreferences = flow(
  fromLocalStorage(LocalStorageKey.PagedViewVisibilityPreferences),
  O.fromNullable,
  O.chain(safeParseJson<SavedVisibilityPreferencesDictionary>())
);

const getSavedColumnVisibilityPreferencesForUserByPagedView = (userId: string, pagedViewKey: string) => {
  const savedColumnPreferencesOption = getSavedColumnVisibilityPreferences();

  return pipe(
    savedColumnPreferencesOption,
    O.map(savedColumnPreferences => savedColumnPreferences[userId]),
    O.chain(O.fromNullable),
    O.map(savedColumnPreferencesForUser => savedColumnPreferencesForUser[pagedViewKey]),
    O.chain(O.fromNullable),
    O.map(preferences => I.Map<string, boolean>(preferences))
  );
};

const Table = <TData extends {}>(props: TableProps<TData>) => {
  const {pagedViewKey, additionalFilterProperties, columns, actions, data, sort, onChangeSort, titleOrType} = props;
  const dispatch = useDispatch();
  const {searchCriteriaValue} = useSelector((state: ReducerState) => state.policyCorrespondence);
  const {userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const defaultVisibleColumnsWithoutPreferences: I.Map<string, boolean> = pipe(
    columns.filter(col => col.allowHide).map(col => ({[col.title]: !col.hiddenByDefault})),
    A.reduce({}, (acc, col) => ({...acc, ...col})),
    I.OrderedMap
  ) as I.Map<string, boolean>;
  const defaultVisibleColumns = isLoaded(userClaims)
    ? pipe(
        getSavedColumnVisibilityPreferencesForUserByPagedView(userClaims.Data.id, pagedViewKey),
        O.getOrElse(() => defaultVisibleColumnsWithoutPreferences)
      )
    : defaultVisibleColumnsWithoutPreferences;

  const defaultColumnWidths: I.Map<string, number> = pipe(
    columns.map(column => ({[column.title]: (1 / columns.length) * 100})),
    A.reduce({}, (acc, col) => ({...acc, ...col})),
    I.OrderedMap
  ) as I.Map<string, number>;

  const [columnWidths, setColumnWidths] = useState(defaultColumnWidths);
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const columnsToShow = columns.filter(col => visibleColumns.get(col.title) || !col.allowHide);

  const [height, setHeight] = useState(0);
  const [filterValue, setFilterValue] = useState(searchCriteriaValue);
  const [totalWidth, setTotalWidth] = useState(0);
  const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState(false);
  const classes = useSnackBarStyles();
  const alertStyle1: React.CSSProperties = {position: 'relative', fontSize: '16px', lineHeight: '20px', fontWeight: "700", paddingBottom: "10px"};
  const alertStyle2: React.CSSProperties = {position: 'relative', fontSize: '14px', lineHeight: '18px', textAlign: "left"};


  useEffect(() => {
    const newWidthPerColumn = (1 / columnsToShow.length) * 100;
    const newColumnWidths: I.Map<string, number> = pipe(
      columns.map(column => ({[column.title]: newWidthPerColumn})),
      A.reduce({}, (acc, col) => ({...acc, ...col})),
      I.OrderedMap
    ) as I.Map<string, number>;
    setColumnWidths(newColumnWidths);
  }, [totalWidth, columns, columnsToShow.length]);

  useEffect(() =>{
    setFilterValue(searchCriteriaValue);
  }, [searchCriteriaValue])

  const handleFilter = (value: string, label: string) => {
    setFilterValue(values=>({
      ...values,
      [label]: value
    }));
  }

  const emptyFilter = Object.values(filterValue).every(
    value => value === ''
  );

  return (
    <ColumnWidthsContext.Provider value={columnWidths}>
      {pagedViewKey.includes("correspondence") &&
      <div className='filter-bar'>
        <Alert severity='info' className='filter__info'>
          Filter using either Account Number or Policy Number.
        </Alert>
        <div className="filter__button__correspondence">
          <div className='select-filters'>
            <div className='filter-label'>Filter By:</div>
            <div className='filters'>
              <TextField style={{cursor: 'pointer'}} placeholder="" value={filterValue.accountNumber} label="Account Number" onChange={e => handleFilter(e.target.value, "accountNumber")}/>
            </div>
            <div className='filters'>
              <TextField style={{cursor: 'pointer'}} placeholder="" value={filterValue.policyNumber} label="Policy Number" onChange={e => handleFilter(e.target.value, "policyNumber")}/>
            </div>
          </div>
          <Button
            variant="contained"
            disabled={emptyFilter}
            onClick={() => {
              dispatch(changeCorrespondenceFilter(filterValue));
            }}
            color="primary">Search & Filter
          </Button>
        </div>
      </div>}
      <Paper className="paged-table__paper" square>
        <VisibleColumnsDrawer
          columnsMap={visibleColumns}
          onClose={() => setIsColumnDrawerOpen(false)}
          open={isColumnDrawerOpen}
          onColumnVisibilityChange={(key, isVisible) => {
            setVisibleColumns(visibleColumns.set(key, isVisible));
          }}
          pagedViewKey={pagedViewKey}
        />
        <MaterialTable component="div" className="paged-table" role="table"
          aria-colcount={columns.length}
          aria-rowcount={data.key === 'Loaded' ? data.Data.totalCount : 0}
          aria-label={`${pagedViewKey} list`}>
          <div className="paged-table__head">
            <TableHeader
              onChangeSort={onChangeSort}
              titleOrType={titleOrType}
              sort={sort}
              actions={actions}
              eventAction={props.eventAction}
              additionalFilterProperties={additionalFilterProperties}
              isScrollbarShowing={isLoaded(data) && isScrollbarShowing(height, data.Data.totalCount)}
              onOpenColumnDrawer={() => setIsColumnDrawerOpen(true)}
              columnsForHeaders={columnsToShow}
              allColumns={columns}
              onColumnResize={(columnTitle, newWidthDelta) => {
                const currentWidth = columnWidths.get(columnTitle);
                const titleOfNextColumn: string = columnWidths
                  .skipUntil((width, title) => title === columnTitle)
                  .skip(1)
                  .keys()
                  .next().value;
                const nextColumnCurrentWidth = columnWidths.get(titleOfNextColumn);
                const toCurrentPixelsOfTotal = (percentWidth: number) => totalWidth / (100 / percentWidth);

                if (currentWidth && nextColumnCurrentWidth) {
                  const newWidths = columnWidths
                    .set(columnTitle, ((toCurrentPixelsOfTotal(currentWidth) + newWidthDelta) / totalWidth) * 100)
                    .set(
                      titleOfNextColumn,
                      ((toCurrentPixelsOfTotal(nextColumnCurrentWidth) - newWidthDelta) / totalWidth) * 100
                    );
                  setColumnWidths(newWidths);
                }
              }}
            />
          </div>
          <MaterialTableBody className="paged-table__body" component="div">
            {matchAsyncPage(props.data, {
              Conflict: () => <div className="paged-table__content">An error has occurred.</div>,
              Loaded: page =>
                page.totalCount === 0 ? (
                  NoResultsMessage
                ) : (
                  <TableBody
                    setWidth={setTotalWidth}
                    setHeight={setHeight}
                    loadMore={props.loadMore}
                    data={page}
                    columns={columnsToShow}
                    pagedViewKey={pagedViewKey}
                  />
                ),
              ServerError: () => <div className="paged-table__content">An error has occurred.</div>,
              NoData: () => NoResultsMessage,
              NoContent: () => NoResultsMessage,
              Unauthorized: () => (
                <div className="paged-table__content">You are not authorized to access this resource.</div>
              ),
              Loading: () => (
                <div className="paged-table__content" aria-busy="true">
                  <Loading />
                </div>
              ),
              NotStarted: () => (
                <Snackbar
                  key={`snackbar_Schoox_loading`}
                  anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                  open={true}
                  className={classes.snackBar}
                >
                  <Alert
                    severity='info'
                    variant="filled"
                  className={classes.alertInfo}
                  >
                    <Box style={{display: 'flex', flexDirection: 'column'}}>
                      <Box style={{display: 'flex', flexDirection: 'column'}}>
                        <Typography variant="h1" style={alertStyle1}>
                          {props.data["message"]}
                        </Typography>
                        <Typography variant="body2" style={alertStyle2}>{props.data["errorDetails"]}</Typography>
                      </Box>
                    </Box>
                  </Alert>
                </Snackbar>
              )
            })}
          </MaterialTableBody>
          <TableFooter className="paged-table__footer" component="div">
            {props.data.key === 'LoadingAdditional' || props.data.key === 'Loaded' ? (
              <div role="row">
                <span id={`table-footer-results-${pagedViewKey}`} role="cell" aria-busy={false}>
                  Total Results: {props.data.Data.totalCount}
                </span>
              </div>
            ) : null}
          </TableFooter>
        </MaterialTable>
      </Paper>
    </ColumnWidthsContext.Provider>
  );
};

export default withRouter(Table);
